/** Row shapes as stored in the production schema (see supabase/migrations). */
export type MonthStatus = 'draft' | 'review' | 'published';

export interface Month {
  id: string;
  month_start: string;
  status: MonthStatus;
  investment_target: number | null;
  investment_actual: number | null;
  personal_spend_cap: number | null;
  personal_spend_actual: number | null;
  lifestyle_spend_cap: number | null;
  lifestyle_spend_actual: number | null;
  liquid_reserve_target: number | null;
  liquid_reserve_actual: number | null;
  monthly_brief: string | null;
  next_best_action: string | null;
  published_at: string | null;
}

export type CategoryType =
  | 'lifestyle' | 'fixed' | 'investment' | 'transfer' | 'business' | 'income' | 'exceptional' | 'other' | 'property';

export interface Category {
  id: string;
  name: string;
  category_type: CategoryType;
  monthly_cap: number | null;
  is_active: boolean;
}

export interface CategoryMonth {
  category_id: string;
  budget_amount: number | null;
  actual_amount: number | null;
  name: string;
  category_type: CategoryType;
}

export interface Transaction {
  id: string;
  tx_date: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  currency: string;
  classification: string | null;
  category_name: string | null;
  account_label: string | null;
  needs_review: boolean;
  /** Present when read from the live database; used for decision maths. */
  category_type?: CategoryType | null;
  is_internal_transfer?: boolean;
  is_business_funding?: boolean;
}

export interface Account {
  id: string;
  name: string;
  institution: string | null;
  account_type: string | null;
  currency: string;
  current_balance: number | null;
  /** Date the balance was true (a statement's month end). Absent before 2026-10-10 data. */
  balance_as_of?: string | null;
  /** Set when this personal account's bank alerts feed provisional rows (migration 20261011000000). */
  alert_bank?: string | null;
  updated_at: string;
}

/** A transaction seen in a bank alert email, before the statement confirms it. */
export interface ProvisionalTransaction {
  id: string;
  account_id: string;
  account_label: string | null;
  bank: string;
  tx_date: string;
  occurred_at: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  currency: string;
  status: 'provisional' | 'matched' | 'unmatched';
  balance_after: number | null;
}

/** Investments held at a bank (money market, ARM) are wealth, not cash. */
const INVESTMENT_ACCOUNT_TYPES = new Set(['investment', 'money_market', 'mutual_fund', 'fixed_deposit', 'pension']);
export const isInvestmentAccount = (a: Pick<Account, 'account_type'>): boolean =>
  INVESTMENT_ACCOUNT_TYPES.has((a.account_type ?? '').toLowerCase());

/** When an account balance was last known to be true. */
export const balanceAsOf = (a: Pick<Account, 'balance_as_of' | 'updated_at'>): string => a.balance_as_of ?? a.updated_at;

export interface CapitalPosition {
  id: string;
  name: string;
  position_type: 'property' | 'business';
  currency: string;
  purchase_price: number | null;
  amount_paid: number | null;
  outstanding_amount: number | null;
  current_value: number | null;
  owner_funding: number | null;
  amount_returned: number | null;
  notes: string | null;
  updated_at: string;
}

export type UploadStatus = 'uploaded' | 'processing' | 'processed' | 'failed' | 'superseded';

export interface Upload {
  id: string;
  month_id: string | null;
  account_id: string | null;
  storage_path: string;
  original_filename: string;
  file_size_bytes: number | null;
  processing_status: UploadStatus;
  processing_error: string | null;
  transaction_count: number | null;
  uploaded_at: string;
  processed_at: string | null;
}

export interface ReviewItem {
  id: string;
  question: string | null;
  tx_date: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  currency: string;
  classification: string | null;
}

export interface ImportRow {
  tx_date: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  currency: string;
  category_id: string | null;
  classification: string;
  confidence: number;
  is_internal_transfer: boolean;
  is_reimbursement: boolean;
  is_business_funding: boolean;
  needs_review: boolean;
  source_hash: string;
}

export interface ImportResult {
  rows: number;
  inserted: number;
  duplicates: number;
  review_items: number;
}

/** The owner's record that he moved a sealed month's leftover (e.g. to GT Money Market). */
export interface InvestmentMove {
  id: string;
  from_month: string;
  amount: number;
  destination: string;
  moved_on: string;
  note: string | null;
}
